import { useEffect, useState } from "react";
import { cotAxios, COTACAO_API } from "./api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, Plus, Pencil, Trash2, Search, Phone, MapPin, FileText, FileDown, ChevronDown, ChevronRight, Eye } from "lucide-react";
import useCotacaoUser from "./useCotacaoUser";

const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("pt-BR"); } catch { return iso; } };

function HistoricoPedido({ pedido }) {
  const [aberto, setAberto] = useState(false);
  const baixarPdf = async (e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem("cotacao_token");
      const res = await fetch(`${COTACAO_API}/cotacao/pedidos/${pedido.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `pedido-${String(pedido.numero).padStart(4, "0")}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error("Erro ao baixar PDF"); }
  };
  return (
    <div className="border rounded-lg overflow-hidden" data-testid={`historico-pedido-${pedido.id}`}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setAberto((s) => !s)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setAberto((s) => !s); } }}
        className="w-full p-3 flex items-center justify-between gap-2 bg-slate-50 hover:bg-slate-100 text-left cursor-pointer"
        data-testid={`toggle-historico-${pedido.id}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {aberto ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
          <div className="min-w-0">
            <div className="font-medium text-sm">Pedido nº {String(pedido.numero).padStart(4, "0")} · Fornec #{String(pedido.numero_fornecedor).padStart(4, "0")}</div>
            <div className="text-xs text-slate-500">
              Cotação nº {pedido.cotacao_numero} · {fmtDate(pedido.created_at)} · {(pedido.itens || []).length} item(ns)
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="text-right">
            <div className="text-sm font-semibold text-blue-700">{brl(pedido.total_geral)}</div>
            {pedido.economia_total > 0 && <div className="text-[10px] text-emerald-700">Econ. {brl(pedido.economia_total)}</div>}
          </div>
          <Button size="sm" variant="outline" onClick={baixarPdf} data-testid={`btn-pdf-hist-${pedido.id}`}>
            <FileDown className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
      {aberto && (
        <div className="p-3 text-sm bg-white border-t space-y-2">
          <table className="w-full text-xs">
            <thead className="text-slate-500 border-b">
              <tr>
                <th className="text-left py-1">Cód.</th>
                <th className="text-left py-1">Item</th>
                <th className="text-right py-1">Qtd</th>
                <th className="text-right py-1">Preço</th>
                <th className="text-right py-1">Total</th>
                <th className="text-left py-1">Pgto</th>
                <th className="text-left py-1">Frete</th>
              </tr>
            </thead>
            <tbody>
              {(pedido.itens || []).map((it, i) => {
                const fp = it.forma_pgto || {};
                const pgto = fp.tipo === "avista" ? "À vista" : fp.tipo === "boleto" ? "Boleto" : fp.tipo === "prazo" ? `Prazo ${(fp.parcelas || []).map((p) => p.dias).join("/")}d` : "—";
                return (
                  <tr key={i} className="border-b">
                    <td className="py-1 font-mono text-slate-500">{(it.produto || {}).codigo || ""}</td>
                    <td className="py-1">{(it.produto || {}).nome}</td>
                    <td className="py-1 text-right">{it.qtd} {(it.produto || {}).unidade || ""}</td>
                    <td className="py-1 text-right">{brl(it.preco)}</td>
                    <td className="py-1 text-right font-medium">{brl(it.total)}</td>
                    <td className="py-1">{pgto}</td>
                    <td className="py-1">{it.frete || ""}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="font-semibold border-t">
                <td colSpan="4" className="py-1 text-right">Total do pedido:</td>
                <td className="py-1 text-right text-blue-700">{brl(pedido.total_geral)}</td>
                <td colSpan="2"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

const emptyForm = {
  razao_social: "", cnpj: "", nome_fantasia: "", celular: "",
  inscricao_estadual: "", nome_vendedor: "", endereco: "", observacoes: "",
};

export default function CotacaoFornecedoresPage() {
  const { canWrite, canDelete, isAuditor } = useCotacaoUser();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [delOpen, setDelOpen] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  useEffect(() => { carregar(); }, []);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await cotAxios.get("/cotacao/fornecedores");
      setRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar");
    } finally { setLoading(false); }
  };

  const abrirNovo = () => { setForm(emptyForm); setEditingId(null); setDialogOpen(true); };
  const abrirEdit = (f) => {
    const clean = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v ?? ""]));
    setForm({ ...emptyForm, ...clean });
    setEditingId(f.id);
    setDialogOpen(true);
  };

  const salvar = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) await cotAxios.put(`/cotacao/fornecedores/${editingId}`, form);
      else await cotAxios.post("/cotacao/fornecedores", form);
      toast.success(editingId ? "Fornecedor atualizado" : "Fornecedor criado");
      setDialogOpen(false);
      carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  const excluir = async () => {
    try {
      await cotAxios.delete(`/cotacao/fornecedores/${delTarget.id}`);
      toast.success("Excluído");
      setDelOpen(false); setDelTarget(null);
      carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir");
    }
  };

  const abrirDetalhe = async (f) => {
    try {
      const { data } = await cotAxios.get(`/cotacao/fornecedores/${f.id}`);
      setDetail(data); setDetailOpen(true);
    } catch (e) {
      toast.error("Erro ao abrir");
    }
  };

  const filtered = rows.filter((r) => {
    const q = busca.toLowerCase().trim();
    if (!q) return true;
    return [r.razao_social, r.nome_fantasia, r.cnpj, r.nome_vendedor, r.celular].some((v) => (v || "").toLowerCase().includes(q));
  });

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="cot-fornecedores-page">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Users className="w-6 h-6 text-blue-700" /> Fornecedores</h1>
          <p className="text-sm text-slate-500">Total: {rows.length}{isAuditor && " · modo somente leitura (Auditor)"}</p>
        </div>
        {canWrite && (
          <Button onClick={abrirNovo} className="bg-blue-700 hover:bg-blue-800" data-testid="btn-novo-fornecedor">
            <Plus className="w-4 h-4 mr-1" /> Novo fornecedor
          </Button>
        )}
      </div>

      <div className="mb-4 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
        <Input placeholder="Buscar por razão social, CNPJ, vendedor..." value={busca} onChange={(e) => setBusca(e.target.value)} className="pl-9" data-testid="busca-fornecedor" />
      </div>

      {loading ? (
        <div className="text-center py-10 text-slate-500">Carregando...</div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500">Nenhum fornecedor cadastrado.</CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {filtered.map((f) => (
            <Card key={f.id} data-testid={`fornecedor-${f.id}`} className="hover:border-blue-300 transition cursor-pointer" onClick={() => abrirDetalhe(f)}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[240px]">
                    <div className="font-semibold">{f.razao_social}</div>
                    {f.nome_fantasia && <div className="text-sm text-slate-600">{f.nome_fantasia}</div>}
                    <div className="text-xs text-slate-500 mt-1 flex gap-4 flex-wrap">
                      {f.cnpj && <span>CNPJ: {f.cnpj}</span>}
                      {f.nome_vendedor && <span>Vendedor: {f.nome_vendedor}</span>}
                      {f.celular && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {f.celular}</span>}
                    </div>
                    {f.endereco && <div className="text-xs text-slate-500 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> {f.endereco}</div>}
                  </div>
                  <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                    {canWrite && (
                      <Button size="sm" variant="outline" onClick={() => abrirEdit(f)} data-testid={`btn-edit-${f.id}`}><Pencil className="w-4 h-4" /></Button>
                    )}
                    {canDelete && (
                      <Button size="sm" variant="destructive" onClick={() => { setDelTarget(f); setDelOpen(true); }} data-testid={`btn-del-${f.id}`}><Trash2 className="w-4 h-4" /></Button>
                    )}
                    {isAuditor && (
                      <Button size="sm" variant="outline" onClick={() => abrirDetalhe(f)} title="Visualizar detalhes"><Eye className="w-4 h-4" /></Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Criar/Editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="dialog-fornecedor">
          <DialogHeader><DialogTitle>{editingId ? "Editar fornecedor" : "Novo fornecedor"}</DialogTitle><DialogDescription>Preencha os dados do fornecedor. A Razão Social é obrigatória.</DialogDescription></DialogHeader>
          <form onSubmit={salvar} className="space-y-3">
            <div><Label>Razão Social*</Label><Input required value={form.razao_social} onChange={(e) => setForm({ ...form, razao_social: e.target.value })} data-testid="in-razao" /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>CNPJ</Label><Input value={form.cnpj} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} /></div>
              <div><Label>Inscrição Estadual</Label><Input value={form.inscricao_estadual} onChange={(e) => setForm({ ...form, inscricao_estadual: e.target.value })} /></div>
            </div>
            <div><Label>Nome Fantasia</Label><Input value={form.nome_fantasia} onChange={(e) => setForm({ ...form, nome_fantasia: e.target.value })} /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Nome do Vendedor</Label><Input value={form.nome_vendedor} onChange={(e) => setForm({ ...form, nome_vendedor: e.target.value })} /></div>
              <div><Label>Celular</Label><Input value={form.celular} onChange={(e) => setForm({ ...form, celular: e.target.value })} /></div>
            </div>
            <div><Label>Endereço</Label><Input value={form.endereco} onChange={(e) => setForm({ ...form, endereco: e.target.value })} /></div>
            <div><Label>Observações</Label><Input value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="btn-salvar-fornecedor">{saving ? "Salvando..." : "Salvar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detalhe / Histórico */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="dialog-fornecedor-detalhe">
          <DialogHeader><DialogTitle>{detail?.razao_social}</DialogTitle><DialogDescription>Detalhes do fornecedor e histórico de pedidos.</DialogDescription></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid sm:grid-cols-2 gap-2">
                {detail.cnpj && <div><b>CNPJ:</b> {detail.cnpj}</div>}
                {detail.inscricao_estadual && <div><b>IE:</b> {detail.inscricao_estadual}</div>}
                {detail.nome_fantasia && <div><b>Fantasia:</b> {detail.nome_fantasia}</div>}
                {detail.nome_vendedor && <div><b>Vendedor:</b> {detail.nome_vendedor}</div>}
                {detail.celular && <div><b>Celular:</b> {detail.celular}</div>}
              </div>
              {detail.endereco && <div><b>Endereço:</b> {detail.endereco}</div>}
              {detail.observacoes && <div><b>Obs:</b> {detail.observacoes}</div>}

              <div className="pt-3 border-t">
                <div className="font-semibold flex items-center gap-2 mb-2"><FileText className="w-4 h-4" /> Histórico de compras ({(detail.historico || []).length})</div>
                {(!detail.historico || detail.historico.length === 0) ? (
                  <div className="text-slate-500 text-sm italic">Nenhum pedido gerado ainda para este fornecedor.</div>
                ) : (
                  <div className="space-y-2 max-h-[420px] overflow-y-auto">
                    {detail.historico.map((h) => <HistoricoPedido key={h.id} pedido={h} />)}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Excluir */}
      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Excluir fornecedor</DialogTitle><DialogDescription>Confirme a exclusão do fornecedor selecionado.</DialogDescription></DialogHeader>
          <div className="text-sm">Excluir <b>{delTarget?.razao_social}</b>? Esta ação não pode ser desfeita.</div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelOpen(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={excluir} data-testid="btn-confirmar-excluir-forn">Excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
